/**
 * CollabsTopMenu
 *
 * Top navigation bar for the CollabsView page. Renders the view title and the
 * contextual action buttons (export, add externe, add warning, add special case).
 * Switching sub-view is done from the sidebar submenu, not from here.
 *
 * Adapts its layout based on the isMobile value (1=desktop, 2=mobile, 3=tablet).
 */
import React from "react";
import { Col, Row } from "react-bootstrap";
import Langs from "../../app/lang/langs";
import { checkPermission } from "../../app/utils/helpers";
import { config } from "../../app/utils/config";
import ImageButton from "../../components/buttons/ImageButton";
import exportIcon from "../../assets/images/export.png";
import exportIconFocus from "../../assets/images/exportFocus.png";
import addIcon from "../../assets/images/add.png";
import addIconFocus from "../../assets/images/addFocus.png";

/* ── Shared inline style tokens ── */
const transition = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

const titleStyle = {
  color: "rgb(30, 30, 30)",
  fontWeight: 700,
  letterSpacing: "-0.02em",
  transition,
};

const actionBtnStyle = {
  borderRadius: 10,
  padding: "4px 6px",
  transition,
};

/**
 * @param {Object} props
 * @param {number} props.isMobile - 1=desktop, 2=mobile, 3=tablet
 * @param {Array} props.viewsOptions - Array of {label, value} sub-views, used for the title
 * @param {number} props.selectedView - Currently active view index
 * @param {Function} props.onExport - Called when export button is clicked
 * @param {Function} props.onAddExterne - Called when "Add externe" is clicked
 * @param {Function} props.onAddWarning - Called when "Add warning" is clicked
 * @param {Function} props.onAddSpecialCase - Called when "Add special case" is clicked
 */
export default function CollabsTopMenu({
  isMobile,
  viewsOptions,
  selectedView,
  onExport,
  onAddExterne,
  onAddWarning,
  onAddSpecialCase,
  onAddEmploymentPromise,
}) {
  const hasWarningsEdit = checkPermission(config.scopes.warnings, config.permissionTypes.edit);
  const hasCollabsEdit = checkPermission(config.scopes.collabs, config.permissionTypes.edit);
  const hasClientTeamsEdit = checkPermission(config.scopes.clientsTeams, config.permissionTypes.edit);

  const selectedOption = viewsOptions.find((o) => o.value == selectedView);

  if (isMobile == 1) {
    return (
      <Row style={{ alignItems: "center" }}>
        <Col md={3}>
          <span className="headerTitle" style={titleStyle}>
            {selectedOption?.label}
          </span>
        </Col>
        {/* The sub-views moved to the sidebar submenu under "Collabs & Suivis", so this
            column is now only the spacer that keeps the title and the actions apart. */}
        <Col md={6} />
        <Col
          md={3}
          className="d-flex justify-content-end pe-4 align-items-center p-0 m-0 ms-auto"
          style={{ gap: 6 }}
        >
          <div style={actionBtnStyle}>
            <ImageButton
              image={exportIcon}
              imageFocus={exportIconFocus}
              onClick={onExport}
              active={true}
              tooltip="Export"
              width={32}
            />
          </div>
          {hasCollabsEdit && hasClientTeamsEdit && selectedView == 0 && (
            <div style={actionBtnStyle}>
              <ImageButton
                image={addIcon}
                imageFocus={addIconFocus}
                onClick={onAddExterne}
                active={true}
                tooltip={<Langs str="ADD_EXTERNE" />}
                width={32}
              />
            </div>
          )}
          {hasWarningsEdit && selectedView == 1 && (
            <div style={actionBtnStyle}>
              <ImageButton
                image={addIcon}
                imageFocus={addIconFocus}
                onClick={onAddWarning}
                active={true}
                tooltip={<Langs str="ADD_WARNING" />}
                width={32}
              />
            </div>
          )}
          {hasCollabsEdit && selectedView == 2 && (
            <div style={actionBtnStyle}>
              <ImageButton
                image={addIcon}
                imageFocus={addIconFocus}
                onClick={onAddSpecialCase}
                active={true}
                tooltip={<Langs str="ADD" />}
                width={32}
              />
            </div>
          )}
          {hasCollabsEdit && selectedView == 3 && (
            <div style={actionBtnStyle}>
              <ImageButton
                image={addIcon}
                imageFocus={addIconFocus}
                onClick={onAddEmploymentPromise}
                active={true}
                tooltip={<Langs str="ADD_EMPLOYMENT_PROMISE" />}
                width={32}
              />
            </div>
          )}
        </Col>
      </Row>
    );
  }

  if (isMobile == 2 || isMobile == 3) {
    return (
      <Row style={{ alignItems: "center" }}>
        <Col md={5}>
          <span className="headerTitle" style={titleStyle}>
            {selectedOption?.label}
          </span>
        </Col>
        <Col
          md={7}
          className="d-flex justify-content-end align-items-center m-0 mt-3"
        >
          <div style={{ display: "flex", gap: 6 }}>
            <div style={actionBtnStyle}>
              <ImageButton
                image={exportIcon}
                imageFocus={exportIconFocus}
                onClick={onExport}
                active={true}
                tooltip="Export"
                width={32}
              />
            </div>
            {hasWarningsEdit && selectedView == 1 && (
              <div style={actionBtnStyle}>
                <ImageButton
                  image={addIcon}
                  imageFocus={addIconFocus}
                  onClick={onAddWarning}
                  active={true}
                  tooltip={<Langs str="ADD_WARNING" />}
                  width={32}
                />
              </div>
            )}
          </div>
        </Col>
      </Row>
    );
  }

  return null;
}
